import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, PackageCheck } from "lucide-react";
import { trackOrder, submitProofDecision, payBalance, money, API } from "@/lib/api";

const STAGES = [
  { id: "received", label: "Order Received", desc: "Your order is in our queue" },
  { id: "production", label: "In Production", desc: "The press is running your job" },
  { id: "ready", label: "Ready for Pickup / Shipped", desc: "Packed and waiting, or on its way" },
  { id: "completed", label: "Completed", desc: "Delivered. Print loud." },
];

const PAYMENT_LABELS = {
  paid: { label: "Paid in Full", cls: "border-emerald-400/60 text-emerald-400" },
  deposit_paid: { label: "Deposit Paid — Balance Outstanding", cls: "border-brand-yellow/60 text-brand-yellow" },
  awaiting_payment: { label: "Awaiting Payment", cls: "border-brand-magenta/60 text-brand-magenta" },
  pending: { label: "Online Payment Pending", cls: "border-white/20 text-zinc-400" },
  expired: { label: "Expired — Unpaid", cls: "border-brand-magenta/60 text-brand-magenta" },
  failed: { label: "Payment Failed — Try Again", cls: "border-brand-magenta/60 text-brand-magenta" },
};

const normalize = (s) => (STAGES.some((x) => x.id === s) ? s : "received");

const Track = () => {
  const [orderId, setOrderId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [proofNote, setProofNote] = useState("");
  const [proofBusy, setProofBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);

  const payBalanceOnline = async () => {
    if (!result) return;
    setPayBusy(true);
    try {
      const res = await payBalance(result.id);
      if (res.payment_url) {
        toast.success("Opening WiPay secure checkout…", { description: `Balance ${money(res.amount)}` });
        window.location.assign(res.payment_url);
        return;
      }
      toast.error("Could not start balance payment — try again");
    } catch (err) {
      toast.error("Could not start balance payment", {
        description: err?.response?.data?.detail || "Try again in a moment",
      });
    } finally {
      setPayBusy(false);
    }
  };

  const decideProof = async (decision) => {
    if (!result) return;
    setProofBusy(true);
    try {
      await submitProofDecision(result.id, decision, proofNote);
      setResult((r) => ({ ...r, proof_status: decision }));
      toast.success(
        decision === "approved" ? "Proof approved — cleared for production" : "Change request sent to the studio"
      );
    } catch {
      toast.error("Could not submit — try again");
    } finally {
      setProofBusy(false);
    }
  };

  const lookup = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      setResult(await trackOrder(orderId.trim()));
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "No order found with that ID — double-check your confirmation (e.g. TLF-1A2B3C4D)"
          : "Could not check right now. Try again in a moment."
      );
    } finally {
      setLoading(false);
    }
  };

  const activeIdx = result ? STAGES.findIndex((s) => s.id === normalize(result.status)) : -1;

  return (
    <div data-testid="track-page" className="mx-auto max-w-2xl px-4 pb-24 pt-40 sm:px-6">
      <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-cyan">Live Order Status</p>
      <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
        Track Your Order
      </h1>
      <p className="mt-3 text-sm text-zinc-400">
        Enter the order ID from your confirmation (starts with TLF-).
      </p>

      <form onSubmit={lookup} className="mt-8 flex gap-3">
        <input
          data-testid="track-order-input"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="TLF-XXXXXXXX"
          className="flex-1 border border-white/15 bg-ink-2 px-4 py-3.5 font-mono text-sm uppercase tracking-widest text-white placeholder-zinc-600 outline-none focus:border-brand-cyan"
        />
        <button
          data-testid="track-order-submit"
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 bg-brand-yellow px-6 font-display text-sm font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:opacity-50"
        >
          <Search className="h-4 w-4" /> Track
        </button>
      </form>

      {error && (
        <p data-testid="track-order-error" className="mt-4 border border-brand-magenta/40 bg-brand-magenta/10 px-4 py-3 font-mono text-xs uppercase tracking-wider text-brand-magenta">
          {error}
        </p>
      )}

      {result && (
        <motion.div
          data-testid="track-order-result"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 border border-white/10 bg-ink-2"
        >
          <div className="h-1 cmyk-line" />
          <div className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-lg font-bold text-brand-yellow">{result.id}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Placed {new Date(result.created_at).toLocaleDateString("en-JM", { dateStyle: "medium" })}
              </span>
            </div>
            <ul className="mt-4 space-y-1">
              {(result.items || []).map((i, idx) => (
                <li key={idx} className="font-mono text-xs text-zinc-400">
                  {i.qty}× {i.name}
                  {i.config && <span className="text-brand-cyan"> ({i.config})</span>}
                </li>
              ))}
            </ul>
            <p className="mt-2 font-mono text-xs text-zinc-500">Total {money(result.total)}</p>

            {result.payment_status && result.payment_status !== "manual" && (
              <div data-testid="track-payment-section" className="mt-5 border border-white/10 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    data-testid="track-payment-status"
                    className={`border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${(PAYMENT_LABELS[result.payment_status] || PAYMENT_LABELS.pending).cls}`}
                  >
                    {(PAYMENT_LABELS[result.payment_status] || PAYMENT_LABELS.pending).label}
                  </span>
                  {result.payment_method && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {result.payment_method === "wipay" ? "WiPay" : "Cash"}
                      {result.payment_plan === "deposit" ? " • 50% Deposit Plan" : ""}
                    </span>
                  )}
                </div>

                {result.payment_plan === "deposit" && (
                  <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3 font-mono text-xs">
                    <div className="flex justify-between text-zinc-400">
                      <span>Order Total</span><span>{money(result.total)}</span>
                    </div>
                    <div className="flex justify-between text-zinc-400">
                      <span>Deposit Required</span><span>{money(result.deposit_amount)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-400">
                      <span>Deposit Paid</span><span data-testid="track-deposit-paid">{money(Math.min(result.amount_paid || 0, result.deposit_amount || 0))}</span>
                    </div>
                    <div className="flex justify-between text-brand-magenta">
                      <span>Balance Outstanding</span><span data-testid="track-balance-due">{money(result.balance_due)}</span>
                    </div>
                  </div>
                )}

                {result.payment_status === "awaiting_payment" && (
                  <p data-testid="track-cash-notice" className="mt-3 border-t border-white/10 pt-3 text-xs leading-relaxed text-zinc-300">
                    Cash payments must be made before production begins. Selecting Cash does not
                    mean Cash on Delivery. Your order will remain on hold until your payment has
                    been received and confirmed.
                  </p>
                )}
                {result.payment_status === "expired" && (
                  <p data-testid="track-expired-notice" className="mt-3 border-t border-white/10 pt-3 text-xs leading-relaxed text-brand-magenta">
                    This cash order was not paid within 48 hours and has been cancelled. Contact us
                    on WhatsApp if you still need it.
                  </p>
                )}

                {result.balance_due > 0.009 &&
                  !["expired", "pending"].includes(result.payment_status) && (
                  <button
                    data-testid="track-pay-balance"
                    onClick={payBalanceOnline}
                    disabled={payBusy}
                    className="mt-4 w-full bg-brand-yellow py-3 font-display text-sm font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:opacity-50"
                  >
                    {payBusy ? "Opening WiPay…" : `Pay Balance — ${money(result.balance_due)} via WiPay`}
                  </button>
                )}
                {result.balance_due > 0.009 && result.payment_status === "awaiting_payment" && (
                  <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                    Prefer card? Use the button above — or pay cash in store
                  </p>
                )}

                {(result.payments || []).length > 0 && (
                  <ul className="mt-3 space-y-0.5 border-t border-white/10 pt-3">
                    {result.payments.map((p) => (
                      <li key={p.id} className="font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                        {money(p.amount)} • {p.method === "wipay" ? "WiPay" : "Cash"} • {p.kind} • {p.status}
                        {p.confirmed_at && ` • ${new Date(p.confirmed_at).toLocaleDateString("en-JM", { dateStyle: "medium" })}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {result.proof_status && result.proof && (
              <div
                data-testid="track-proof-section"
                className="mt-6 border border-brand-yellow/40 bg-brand-yellow/5 p-5"
              >
                <p className="font-display text-lg font-black uppercase tracking-wide text-brand-yellow">
                  Artwork Proof
                </p>
                <a
                  href={`${API}/files/${result.proof.storage_path}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="track-proof-view"
                  className="mt-3 block border border-white/15"
                >
                  <img
                    src={`${API}/files/${result.proof.storage_path}`}
                    alt="Artwork proof"
                    data-testid="track-proof-image"
                    className="max-h-72 w-full object-contain bg-ink"
                  />
                </a>
                {result.proof_status === "awaiting_approval" ? (
                  <div className="mt-4">
                    <p className="text-xs leading-relaxed text-zinc-300">
                      Review your proof carefully. Approving sends your order into production — no
                      changes are possible after approval. Need a tweak? Request changes with a note.
                    </p>
                    <textarea
                      data-testid="track-proof-note"
                      value={proofNote}
                      onChange={(e) => setProofNote(e.target.value)}
                      placeholder="Optional note for change requests…"
                      rows={2}
                      className="mt-3 w-full border border-white/15 bg-ink px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:border-brand-yellow"
                    />
                    <div className="mt-3 flex flex-wrap gap-3">
                      <button
                        data-testid="track-proof-approve"
                        onClick={() => decideProof("approved")}
                        disabled={proofBusy}
                        className="bg-emerald-400 px-6 py-3 font-display text-sm font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:opacity-50"
                      >
                        Approve Proof
                      </button>
                      <button
                        data-testid="track-proof-changes"
                        onClick={() => decideProof("changes_requested")}
                        disabled={proofBusy}
                        className="border border-brand-magenta/60 px-6 py-3 font-display text-sm font-black uppercase tracking-widest text-brand-magenta transition-colors hover:bg-brand-magenta hover:text-white disabled:opacity-50"
                      >
                        Request Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    data-testid="track-proof-status"
                    className={`mt-4 inline-block border px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest ${
                      result.proof_status === "approved"
                        ? "border-emerald-400/60 text-emerald-400"
                        : "border-brand-magenta/60 text-brand-magenta"
                    }`}
                  >
                    {result.proof_status === "approved"
                      ? "Proof Approved — cleared for production"
                      : "Changes Requested — the studio is revising your proof"}
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 space-y-0">
              {STAGES.map((s, idx) => {
                const done = idx <= activeIdx;
                const current = idx === activeIdx;
                return (
                  <div key={s.id} data-testid={`track-stage-${s.id}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <span
                        className={`flex h-7 w-7 items-center justify-center border-2 ${
                          current
                            ? "border-brand-yellow bg-brand-yellow text-ink"
                            : done
                              ? "border-brand-cyan bg-brand-cyan/20 text-brand-cyan"
                              : "border-white/15 text-zinc-600"
                        }`}
                      >
                        {done ? <PackageCheck className="h-3.5 w-3.5" /> : <span className="font-mono text-[10px]">{idx + 1}</span>}
                      </span>
                      {idx < STAGES.length - 1 && (
                        <span className={`h-8 w-0.5 ${idx < activeIdx ? "bg-brand-cyan" : "bg-white/10"}`} />
                      )}
                    </div>
                    <div className="pb-6">
                      <p className={`font-display text-base font-bold uppercase tracking-wide ${done ? "text-white" : "text-zinc-600"}`}>
                        {s.label}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{s.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Track;
